import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ContentPageTheme, Form, ReviewWidgetConfig, ReviewWidgetConfigServices } from "@/services/bedrock";
import { ConfigSkeleton } from "../components/LoadingStates";
import { ShowcaseLayout } from "../components/ShowcaseLayout";
import { PreviewPlayerState } from "../components/usePreviewPlayer";
import { EmbeddedHighlightsConfigForm } from "./components/EmbeddedHighlightsConfigForm";
import { EmbeddedHighlightsPreview } from "./components/EmbeddedHighlightsPreview";
import { useEmbeddedHighlightsPreview } from "./components/useEmbeddedHighlightsPreview";

interface ViewProps {
  themes: ContentPageTheme[];
  forms: Form[];
  preview: PreviewPlayerState;
  onHasChangesChange: (hasChanges: boolean) => void;
}

interface EmbeddedHighlightsContentProps extends ViewProps {
  config: ReviewWidgetConfig;
  updateConfig: (updates: Partial<ReviewWidgetConfig>) => void;
}

function EmbeddedHighlightsContent({
  config,
  updateConfig,
  themes,
  forms,
  preview,
  onHasChangesChange,
}: EmbeddedHighlightsContentProps) {
  const previewProps = useEmbeddedHighlightsPreview({
    config,
    themes,
    forms,
    previewPlayer: preview.previewPlayer,
  });

  return (
    <ShowcaseLayout
      settings={
        <EmbeddedHighlightsConfigForm
          config={config}
          updateConfig={updateConfig}
          themes={themes}
          forms={forms}
          onHasChangesChange={onHasChangesChange}
        />
      }
      preview={<EmbeddedHighlightsPreview preview={preview} {...previewProps} />}
    />
  );
}

export function EmbeddedHighlights({ themes, forms, preview, onHasChangesChange }: ViewProps) {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState<ReviewWidgetConfig | null>(null);

  useEffect(() => {
    setLoading(true);
    ReviewWidgetConfigServices.getConfigs()
      .then(async configs => {
        if (configs.length > 0) {
          setConfig(configs[0]);
        } else {
          const created = await ReviewWidgetConfigServices.createConfig({
            approvedForSocialOnly: true,
            reviewSortType: "featured",
            autoScrollEnabled: true,
          });
          setConfig(created);
        }
      })
      .catch(error => {
        console.error(error);
        toast.error("Error loading Embedded Highlights settings");
      })
      .finally(() => setLoading(false));
  }, []);

  const updateConfig = (updates: Partial<ReviewWidgetConfig>) => {
    setConfig(current => (current ? { ...current, ...updates } : current));
  };

  return (
    <div className="flex flex-col gap-4 lg:min-h-0 lg:flex-1">
      {loading || !config ? (
        <ConfigSkeleton />
      ) : (
        <EmbeddedHighlightsContent
          config={config}
          updateConfig={updateConfig}
          themes={themes}
          forms={forms}
          preview={preview}
          onHasChangesChange={onHasChangesChange}
        />
      )}
    </div>
  );
}
